import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import { useAccounts } from '../../context/AccountContext';
import EquityChart from '../../components/EquityChart';
import { Bar, Doughnut } from 'react-chartjs-2';
import { Chart as ChartJS, ArcElement, BarElement, CategoryScale, LinearScale, Tooltip, Legend } from 'chart.js';

ChartJS.register(ArcElement, BarElement, CategoryScale, LinearScale, Tooltip, Legend);

export default function TraderPerformance() {
  const { scopeParams } = useAccounts();
  const [perf, setPerf]     = useState(null);
  const [days, setDays]     = useState(30);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    // Across several accounts the backend sums the daily rows into one
    // portfolio curve — see performance.mergeSnapshotsByDate.
    api.get('/trader/performance', { params: { days, ...scopeParams } })
      .then(r => setPerf(r.data))
      .finally(() => setLoading(false));
  }, [days, scopeParams]);

  if (loading) return <p className="text-white">Loading performance data...</p>;
  if (!perf)   return <p className="text-danger">Failed to load performance.</p>;

  const { snapshots, stats } = perf;

  const winLossData = {
    labels: ['Wins', 'Losses'],
    datasets: [{ data: [stats.winTrades, stats.lossTrades], backgroundColor: ['#00d6b4', '#fd5d93'] }],
  };

  const dailyPnlData = {
    labels: snapshots.map(s => new Date(s.date).toLocaleDateString()),
    datasets: [{
      label: 'Daily P&L',
      data: snapshots.map(s => s.dailyPnl),
      backgroundColor: snapshots.map(s => s.dailyPnl >= 0 ? '#00d6b4' : '#fd5d93'),
    }],
  };

  return (
    <div className="row">
      {/* Stats Cards */}
      <div className="col-md-3 col-lg-2 col-6">
        <div className="card card-stats text-center p-3">
          <p className="card-category">Gross P&L</p>
          <h4 className={`card-title ${stats.totalPnl >= 0 ? 'text-success' : 'text-danger'}`}>
            {stats.totalPnl >= 0 ? '+' : ''}${stats.totalPnl?.toFixed(2) ?? '0.00'}
          </h4>
        </div>
      </div>
      <div className="col-md-3 col-lg-2 col-6">
        <div className="card card-stats text-center p-3">
          <p className="card-category">Total Trades</p>
          <h4 className="card-title">{stats.totalTrades}</h4>
        </div>
      </div>
      <div className="col-md-3 col-lg-2 col-6">
        <div className="card card-stats text-center p-3">
          <p className="card-category">Win Rate</p>
          <h4 className="card-title text-success">{stats.winRate}%</h4>
        </div>
      </div>
      <div className="col-md-3 col-lg-2 col-6">
        <div className="card card-stats text-center p-3">
          <p className="card-category">Profit Factor</p>
          <h4 className="card-title">{stats.profitFactor}</h4>
        </div>
      </div>
      <div className="col-md-3 col-lg-2 col-6">
        <div className="card card-stats text-center p-3">
          <p className="card-category">Max Drawdown</p>
          <h4 className="card-title text-danger">{stats.maxDrawdown}%</h4>
        </div>
      </div>
      <div className="col-md-3 col-lg-2 col-6">
        <div className="card card-stats text-center p-3">
          <p className="card-category">Avg Win</p>
          <h4 className="card-title text-success">+${stats.avgWin}</h4>
        </div>
      </div>
      <div className="col-md-3 col-lg-2 col-6">
        <div className="card card-stats text-center p-3">
          <p className="card-category">Avg Loss</p>
          <h4 className="card-title text-danger">-${stats.avgLoss}</h4>
        </div>
      </div>

      {/* Period selector */}
      <div className="col-12 mb-2">
        <div className="btn-group">
          {[7, 30, 90, 180].map(d => (
            <button key={d} className={`btn btn-sm ${days === d ? 'btn-primary' : 'btn-secondary'}`}
              onClick={() => setDays(d)}>{d}D</button>
          ))}
        </div>
      </div>

      {/* Equity Chart */}
      <div className="col-12">
        <div className="card">
          <div className="card-header"><h5 className="card-title">Equity Curve ({days} days)</h5></div>
          <div className="card-body"><EquityChart snapshots={snapshots} height={280} /></div>
        </div>
      </div>

      {/* Daily P&L bar + Win/Loss donut */}
      <div className="col-md-8">
        <div className="card">
          <div className="card-header"><h5 className="card-title">Daily P&L</h5></div>
          <div className="card-body" style={{ height: 280 }}>
            <Bar data={dailyPnlData} options={{
              responsive: true, maintainAspectRatio: false,
              plugins: { legend: { display: false } },
              scales: {
                x: { ticks: { color: '#9a9a9a' }, grid: { display: false } },
                y: { ticks: { color: '#9a9a9a' }, grid: { color: 'rgba(255,255,255,0.05)' } },
              },
            }} />
          </div>
        </div>
      </div>
      <div className="col-md-4">
        <div className="card">
          <div className="card-header"><h5 className="card-title">Win / Loss</h5></div>
          <div className="card-body d-flex justify-content-center align-items-center" style={{ height: 280 }}>
            {stats.totalTrades > 0 ? (
              <Doughnut data={winLossData} options={{
                responsive: true, plugins: { legend: { labels: { color: '#9a9a9a' } } },
              }} />
            ) : <p className="text-muted">No trade data</p>}
          </div>
        </div>
      </div>
    </div>
  );
}
